// Markdown -> HTML, for previewing a poll the way the community portal renders
// it. A port of d3-kit's `markdown/markdown.js` at v0.50.15 — the version the
// portal vendors — not an import: this repo takes no d3-kit on the web side
// (see CLAUDE.md), and matching the portal's output is the whole point, so the
// pinned version matters more than the newest one (later d3-kit versions add
// blockquotes and rules the portal does not yet render). Keep it in step with
// what d3-community ships.
//
// Input is HTML-escaped before any construct is applied, so a body can never
// inject markup; links are restricted to safe schemes and rendered with
// rel="noopener noreferrer", with unsafe links falling back to plain text
// (protocol-relative `//host` counts as unsafe). Images render as a bare
// `<img src alt loading="lazy" referrerpolicy="no-referrer">`, with a tighter
// `src` allowlist than `href` because an image fetches on render rather than on
// a click. `renderMarkdown` turns the output into a DOM node, which Lit renders
// in place — no unsafeHTML directive needed.

/** @param {string} s */
function escapeHtml(s) {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * The link allowlist: an absolute http(s) URL, a mailto:, a ROOT-relative path,
 * or a fragment. Protocol-relative `//host` is rejected.
 * @param {string} href
 */
function isSafeHref(href) {
    return (
        /^https?:\/\//i.test(href) ||
        /^mailto:/i.test(href) ||
        (href.startsWith('/') && !href.startsWith('//')) ||
        href.startsWith('#')
    );
}

/**
 * The image-source allowlist. Stricter than isSafeHref: `mailto:` and `#` cannot
 * address an image. `data:` and `javascript:` fail by construction.
 * @param {string} src
 */
function isSafeSrc(src) {
    return /^https?:\/\//i.test(src) || (src.startsWith('/') && !src.startsWith('//'));
}

/** @param {string} s */
function emphasis(s) {
    return s
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/__([^_]+)__/g, '<strong>$1</strong>')
        .replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>')
        .replace(/(^|[^_])_([^_]+)_/g, '$1<em>$2</em>')
        .replace(/`([^`]+)`/g, '<code>$1</code>');
}

/** @param {string} s */
function inline(s) {
    // The link/image pass is atomic: each one — a generated anchor or img, or an
    // unsafe URL kept as literal text — is swapped for an opaque token before the
    // emphasis/code passes run, so a `_` or `*` inside a URL can't start an
    // emphasis match. Emphasis inside a safe link's TEXT is still rendered.
    //
    // Images are tokenized first, in their own pass: it consumes the `!` (matched
    // after the link rule, `![alt](url)` would lose its brackets to the link and
    // leave a stray `!`), and it is what makes `[![alt](img)](href)` work — once
    // the image is a bracket-free token, the link rule sees a clean `[token](href)`.
    /** @type {Array<string>} */
    const tokens = [];
    const push = (html) => `\x00${tokens.push(html) - 1}\x00`;
    // `alt` is inserted as-is: the block was escaped before inline() ran, so
    // quotes are already &quot; and it cannot break out of the attribute. No
    // emphasis() — an attribute can't hold markup.
    const withImages = s.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (match, alt, src) =>
        push(
            isSafeSrc(src)
                ? `<img src="${src}" alt="${alt}" loading="lazy" referrerpolicy="no-referrer">`
                : match,
        ),
    );
    const tokenized = withImages.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, text, href) =>
        push(
            isSafeHref(href)
                ? `<a href="${href}" rel="noopener noreferrer">${emphasis(text)}</a>`
                : match,
        ),
    );
    // Resolve until stable: a token's replacement can itself contain a token
    // (an image inside a link's text), and String.replace never re-scans what it
    // just inserted. Bounded by the token count, the deepest nesting possible.
    // `?? m` restores a user-typed NUL sequence whose index is out of range.
    let out = emphasis(tokenized);
    // biome-ignore lint/suspicious/noControlCharactersInRegex: the NUL sentinel is deliberate — nothing in escaped Markdown produces one by accident
    for (let pass = 0; pass <= tokens.length && /\x00\d+\x00/.test(out); pass++) {
        // biome-ignore lint/suspicious/noControlCharactersInRegex: same sentinel
        out = out.replace(/\x00(\d+)\x00/g, (m, i) => tokens[i] ?? m);
    }
    return out;
}

/**
 * Convert a Markdown string to an HTML string.
 * @param {string} text
 * @returns {string}
 */
export function markdownToHtml(text) {
    const blocks = String(text).split(/\n{2,}/);
    /** @type {Array<string>} */
    const out = [];
    for (const block of blocks) {
        if (!block.trim()) continue;
        const lines = block.split('\n');
        const heading = lines.length === 1 && block.match(/^(#{1,6})\s+(.+)$/);
        if (heading) {
            const level = heading[1].length;
            out.push(`<h${level}>${inline(escapeHtml(heading[2]))}</h${level}>`);
            continue;
        }
        const isUnordered =
            lines.some((l) => /^\s*[-*]\s+/.test(l)) &&
            lines.every((l) => /^\s*[-*]\s+/.test(l) || !l.trim());
        if (isUnordered) {
            const items = lines
                .filter((l) => l.trim())
                .map((l) => `<li>${inline(escapeHtml(l.replace(/^\s*[-*]\s+/, '')))}</li>`)
                .join('');
            out.push(`<ul>${items}</ul>`);
            continue;
        }
        const isOrdered =
            lines.some((l) => /^\s*\d+[.)]\s+/.test(l)) &&
            lines.every((l) => /^\s*\d+[.)]\s+/.test(l) || !l.trim());
        if (isOrdered) {
            const items = lines
                .filter((l) => l.trim())
                .map((l) => `<li>${inline(escapeHtml(l.replace(/^\s*\d+[.)]\s+/, '')))}</li>`)
                .join('');
            out.push(`<ol>${items}</ol>`);
            continue;
        }
        out.push(`<p>${inline(escapeHtml(block)).replace(/\n/g, '<br>')}</p>`);
    }
    return out.join('');
}

/**
 * Render a Markdown string to a DOM node Lit can place inline.
 * @param {string} text
 * @returns {HTMLDivElement | ''}
 */
export function renderMarkdown(text) {
    if (!text) return '';
    const container = document.createElement('div');
    container.style.display = 'contents';
    container.innerHTML = markdownToHtml(text);
    return container;
}
